import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { LineReveal, Reveal, scrollToId, EASE } from "@/lib/motion";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/21f7c395-9e08-413f-97be-298a12588442/images/ea3641360eff6602bf7eedd4d8b6da612d79f84966e8ac1528adc7250c69827b.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden spotlight pt-20"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-[calc(100vh-5rem)]">
        {/* Left copy */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10 pt-10 lg:pt-0">
          <Reveal delay={0.1}>
            <div className="flex items-center gap-3 mb-8 font-mono text-[11px] uppercase tracking-[0.25em] text-paper/50">
              <span className="w-6 h-px bg-acid" />
              Revenue operating system
            </div>
          </Reveal>

          <h1 className="font-display font-extrabold leading-[0.92] tracking-[-0.02em] text-[clamp(2.9rem,8vw,7rem)] text-paper">
            <LineReveal
              delay={0.35}
              lines={[
                <>The revenue</>,
                <>
                  engine that <span className="font-serif-i italic font-normal text-acid">thinks</span>
                </>,
                <>ahead of you.</>,
              ]}
            />
          </h1>

          <Reveal delay={1.1}>
            <p className="mt-8 max-w-md text-paper/60 text-base md:text-lg leading-relaxed">
              AXIOM unifies your pipeline, signals and forecasting into one calm,
              decisive surface — so revenue teams stop guessing and start closing.
            </p>
          </Reveal>

          <Reveal delay={1.25}>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <button
                data-testid="hero-book-demo"
                onClick={() => scrollToId("book")}
                className="group relative overflow-hidden rounded-full bg-acid px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink font-bold flex items-center gap-2"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Book a demo <ArrowDownRight className="w-4 h-4 group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
                </span>
                <span className="absolute inset-0 bg-paper scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500 ease-out" />
              </button>
              <button
                onClick={() => scrollToId("manifesto")}
                className="font-mono text-xs uppercase tracking-[0.2em] text-paper/60 hover:text-paper transition-colors border-b border-white/20 hover:border-acid pb-1"
              >
                Read the manifesto
              </button>
            </div>
          </Reveal>
        </motion.div>

        {/* Right image, clipped + spotlight */}
        <div className="lg:col-span-5 relative h-[380px] sm:h-[520px] lg:h-[80vh]">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
            className="absolute inset-0 overflow-hidden rounded-[2px] border border-white/10"
          >
            <motion.img
              src={HERO_IMG}
              alt="AXIOM abstract sculpture"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70">
              <span>Fig. 01 — Signal core</span>
              <span className="text-acid">/ live</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-paper/40 flex flex-col items-center gap-2"
      >
        Scroll
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-px h-8 bg-gradient-to-b from-acid to-transparent"
        />
      </motion.div>
    </section>
  );
};
